import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { describeApiError } from '../../data/http';
import type { SaveUserRequest, UserSummary } from '../../data/types';
import { deleteUser, listUsers, saveUser } from '../../data/users';
import { useAuth } from '../../hooks/useAuth';

const blank: SaveUserRequest = { email: '', displayName: '', roles: [], isActive: true };

const field =
  'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

export function UsersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user: me } = useAuth();

  const [users, setUsers] = useState<UserSummary[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ existingEmail?: string; draft: SaveUserRequest } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    return listUsers()
      .then((response) => {
        setUsers(response.items);
        setRoles(response.roles);
        setError(null);
      })
      .catch((err: unknown) => setError(describeApiError(err, t('settings.users.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const myEmail = me?.userDetails.trim().toLowerCase();
  const isSelf = (email: string) => myEmail !== undefined && email.toLowerCase() === myEmail;
  const editingSelf = editing?.existingEmail !== undefined && isSelf(editing.existingEmail);

  const update = (changes: Partial<SaveUserRequest>) =>
    setEditing((current) => (current ? { ...current, draft: { ...current.draft, ...changes } } : current));

  const toggleRole = (role: string, checked: boolean) => {
    if (!editing) return;
    const next = checked ? [...editing.draft.roles, role] : editing.draft.roles.filter((r) => r !== role);
    update({ roles: next });
  };

  const save = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      const saved = await saveUser(editing.draft, editing.existingEmail);
      toast({ title: t('settings.users.saved'), type: 'success' });
      setEditing({
        existingEmail: saved.email,
        draft: { displayName: saved.displayName, roles: saved.roles, isActive: saved.isActive }
      });
      await load();
    } catch (err) {
      toast({ title: describeApiError(err, t('settings.users.saveFailed')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (target: UserSummary) => {
    const confirmed = await showConfirmModal({
      title: t('settings.users.deleteTitle'),
      description: t('settings.users.deleteDescription', { email: target.email }),
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteUser(target.email);
      if (editing?.existingEmail === target.email) setEditing(null);
      await load();
      toast({ title: t('settings.users.deleted'), type: 'success' });
    } catch (err) {
      toast({ title: describeApiError(err, t('settings.users.deleteFailed')), type: 'error' });
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
            {t('settings.users.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('settings.users.subtitle')}</p>
        </div>

        <button
          type="button"
          onClick={() => setEditing({ draft: { ...blank } })}
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          {t('settings.users.add')}
        </button>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">{t('settings.users.signInNotice')}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t('settings.users.bootstrapNotice')}</p>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <section>
            <h2 className="text-base font-semibold text-foreground">{t('settings.users.listTitle')}</h2>

            {users.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">{t('settings.users.empty')}</p>
            ) : (
              <ul className="mt-3 grid gap-2">
                {users.map((person) => (
                  <li key={person.email} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-surface p-4">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            existingEmail: person.email,
                            draft: { displayName: person.displayName, roles: person.roles, isActive: person.isActive }
                          })
                        }
                        className="break-all text-left font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        {person.displayName || person.email}
                      </button>
                      {person.displayName ? (
                        <p className="mt-0.5 break-all text-sm text-muted-foreground">{person.email}</p>
                      ) : null}
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {person.roles.map((role) => t(`user.roles.${role}`, role)).join(', ') || '-'} ·{' '}
                        {person.isActive ? t('settings.users.active') : t('settings.users.inactive')}
                        {isSelf(person.email) ? ` · ${t('settings.users.you')}` : ''}
                      </p>
                    </div>

                    {isSelf(person.email) ? null : (
                      <button
                        type="button"
                        onClick={() => void remove(person)}
                        aria-label={t('settings.users.deleteLabel', { email: person.email })}
                        className="shrink-0 rounded border border-input bg-surface p-1.5 text-destructive transition hover:bg-destructive-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        <TrashIcon className="size-4" aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {editing ? (
            <section className="rounded-lg border border-border bg-surface p-5">
              <h2 className="text-base font-semibold text-foreground">
                {editing.existingEmail
                  ? t('settings.users.editTitle', { email: editing.existingEmail })
                  : t('settings.users.newTitle')}
              </h2>

              <div className="mt-4 grid gap-3">
                {editing.existingEmail ? null : (
                  <label className="block text-sm">
                    <span className="font-medium text-foreground">{t('settings.users.email')}</span>
                    <input
                      type="email"
                      value={editing.draft.email ?? ''}
                      onChange={(e) => update({ email: e.target.value })}
                      className={field}
                    />
                    <span className="mt-1 block text-xs text-muted-foreground">{t('settings.users.emailHint')}</span>
                  </label>
                )}

                <label className="block text-sm">
                  <span className="font-medium text-foreground">{t('settings.users.displayName')}</span>
                  <input
                    type="text"
                    value={editing.draft.displayName}
                    onChange={(e) => update({ displayName: e.target.value })}
                    className={field}
                  />
                </label>

                <fieldset>
                  <legend className="text-sm font-medium text-foreground">{t('settings.users.roles')}</legend>
                  <div className="mt-2 grid gap-2">
                    {roles.map((role) => (
                      <label key={role} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={editing.draft.roles.includes(role)}
                          disabled={editingSelf && role === 'admin'}
                          onChange={(e) => toggleRole(role, e.target.checked)}
                          className="size-4 rounded border-input"
                        />
                        <span className="text-foreground">{t(`user.roles.${role}`, role)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.draft.isActive}
                    disabled={editingSelf}
                    onChange={(e) => update({ isActive: e.target.checked })}
                    className="size-4 rounded border-input"
                  />
                  <span className="text-foreground">{t('settings.users.active')}</span>
                </label>

                {editingSelf ? (
                  <p className="text-xs text-muted-foreground">{t('settings.users.selfLocked')}</p>
                ) : null}
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={busy}
                  className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
